/* eslint-disable jsx-a11y/label-has-associated-control */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import isEqual from 'react-fast-compare';

import { quoteFormSelector } from 'ctm-selectors';

import {
    get,
    apiAction,
    elementRulesStateToProps,
    rulesCache,
    pageContext,
    nestFlatQuery,
    interpolateString
} from 'ctm-util-functions';

import { getMFASchema } from './mfa_base_schema';
import { SimpleFlexbox } from '../../flexbox/flexbox';

import * as actions from '../actions';
import styles from './multi_field_address.css';
import labelStyles from '../label/label.css';
import validationStyles from '../validation_message/validation_message.css';

const objValues = require('lodash.values');

class _MultiFieldAddress extends Component {

    constructor(props) {
        super(props);

        this.state = {
            suburbs: [],
            touches: {
                postcode: false,
                suburb: false,
                streetName: false,
                streetNumber: false
            }
        };

        this.id = props.schema.backendIdentifier;

        // These are extra functions that can be passed down to each subcomponent in case there's a specific MFA operation
        // such as setting state - or in this case loading suburb options when a postcode is entered - that need to happen
        // when the value for the subcomponent has changed
        this.onChangeFuncs = {
            postcode: (event) => { this.handlePostCodeChange(event); },
            suburb: (event) => { this.handleSuburbSelected(event); }
        };

        // Use these functions to dynamically add schema values to the schema for a subcomponent on a re-render
        // eg suburbs are kept on internal state, so we need to provide them to the suburb select when MFA causes it to re-render
        this.updateSchemaFuncs = {
            // eslint-disable-next-line no-param-reassign, react/destructuring-assignment
            suburb: (elementSchema) => { elementSchema.field.options = this.state.suburbs; }
        };
    }

    /**
     * Check if this component has failed page validation and apply touches to all elements with validation if it has
     * @param {Object} nextProps - The incoming props
     */
    // eslint-disable-next-line react/no-deprecated
    componentWillReceiveProps = (nextProps) => {
        this.setSubComponentTouches(nextProps);
    };

    /**
     * Set the values from the start page
     */
    updateFromStartPage = () => {
        const {
            elementSchema,
            stateValues,
            dispatch,
            schema
        } = this.props;
        const values = {};
        const touches = {};
        for (const row of elementSchema.rows) {
            for (const element of row) {
                if (!element.preloadFrom) {
                    continue;
                }
                values[`${this.id}.${element.name}`] = stateValues[element.name];
                touches[element.name] = true;
            }
        }

        if (stateValues.postcode) {
            this.loadOptions(stateValues.postcode);
        }

        if (Object.keys(values).length) {
            dispatch(actions.setValues(nestFlatQuery(values), schema.stateKey));
            this.setState((prevState) => { return { touches: { ...prevState.touches, ...touches } }; });
        }

        for (const elementName of Object.keys(stateValues)) {
            this.applySetValuesRule(elementName, stateValues[elementName]);
        }
    };

    componentDidMount = () => {
        this.updateFromStartPage();
    };

    /**
     * Set touches for subcomponents when the page has been validated and there are failing MFA rules.
     * We need to 'touch' the elements that correspond with the failing MFA rules - eg postcode for 'fullAddressMatchesInitialPostcode
     * so that the correct validation styles are applied to the elements that are invalid
     * @param {Object} nextProps - The incoming set of props
     * @returns {null}
     */
    // eslint-disable-next-line consistent-return
    setSubComponentTouches = (nextProps) => {
        const valRules = nextProps.rules.validation;
        const { touches } = this.state;

        if (valRules.result) {
            return null;
        }

        const elementRule = rulesCache.getElementRuleForId(valRules.id);
        const valProps = elementRule.props;

        const newTouches = {};
        for (const fieldId of Object.keys(valProps.ruleTouches)) {
            if (touches[fieldId] || nextProps.invalidElements.size) {
                newTouches[fieldId] = true;
            }
        }

        this.setState((state) => {
            return { touches: { ...state.touches, ...newTouches } };
        });
    };

    /**
     * Determine if the component should re-render based on the nextState
     * @param  {Object} nextProps  The incoming set of props
     * @param  {Object} nextState  The incoming state
     * @returns {Boolean}           true if the component should re-render
     */
    shouldComponentUpdate = (nextProps, nextState) => {
        return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
    };

    /**
     * Handle custom events when a value in one of the fields is changed
     * @param {Object} event - The onChange event object
     * @param {String} id - The id of the field that changed
     * @param {String} value - The value of the field that changed
     */

    handleValueChangeFromEvent = (event, id, value) => {
        this.setState((state) => {
            return { touches: { ...state.touches, [id]: true } };
        }, () => {
            const { stateValues, dispatch } = this.props;
            if (get(this.props, 'schema.api.onSetValue')
                && stateValues.postcode
                && stateValues.streetName
                && stateValues.streetNumber
                && stateValues.suburb
                && get(this.props, 'rules.validation.result', true)) {
                // Dispatch the API action for the onSetValue event.
                dispatch(this.getApiAction('onSetValue', stateValues));
            }
        });
        if (value) {
            this.applySetValuesRule(id, value);
            this.elementTouched();
        }
    };

    /**
     * Dispatch the element touched action to indicate that the user has added values to this input
     */
    elementTouched = () => {
        const { dispatch, schema } = this.props;
        dispatch(actions.elementTouched(this.id, schema.stateKey));
    };

    /**
     * Apply the set values rule. Called on mount and when values change, this sets the values into another part of
     * the state tree, eg copying residentialAddress fields to postalAddress fields
     * @param {String} id - the id of the value to copy, eg postcode
     * @param {String} value = the value to copy
     */
    applySetValuesRule = (id, value) => {
        const { rules, dispatch } = this.props;
        if (get(this.props, 'rules.setValues.result')) {
            const elementRule = rulesCache.getElementRuleForId(rules.setValues.id);
            const stateKey = elementRule.props.stateKey;
            const identifier = `${stateKey}.${id}`;
            dispatch(
                actions.setValue(identifier, value, 'vertical.quote')
            );
        }
    };

    /**
     * Return an api action for the given event type
     * @param  {String}  actionType       - The type of api action to construct - onChange or onSetValue
     * @param  {Array}  inputValue       - RESTful args for the request - if dynamic and not loaded from schema
     * @returns {Object|null}      The action to dispatch, or null if there is no config for eventType for this component.
     */
    getApiAction = (actionType, inputValue) => {
        const { schema } = this.props;
        const { api } = schema;
        if (!api) {
            throw new Error(`Action Type ${actionType} was not specified in your AutoCompleteCombo api schema.`);
        }
        const apiSchema = api[actionType];
        const restArgs = apiSchema.type === 'GET' ? [{ value: inputValue }] : [];
        const postArgs = apiSchema.type === 'POST' ? { [apiSchema.requestPayloadValueName]: inputValue || {} } : {};
        const pageState = pageContext.getProps().pageState;
        const action = apiAction(apiSchema, pageState, restArgs, apiSchema.baseUrlName, postArgs);
        action.responseFunc = (response) => {
            this.setOptionsFromApiResponse(response);
        };
        return action;
    };

    /**
     * Set the Options for the auto-complete selector.
     * @param  {Object} response - response to the api request.
     */
    setOptionsFromApiResponse = (response) => {
        const options = [];
        if (response && response.length > 0) {
            for (const result of response) {
                options.push({
                    label: interpolateString(this.config.labelTemplate)(result),
                    value: result
                });
            }
        } else if (response && response.length === 0) {
            options.push({
                label: get(this.props, 'schema.field.noResultsMessage', ''),
                id: `autocomplete.noResults.${get(this.props, 'schema.identifier', '')}`,
                value: '' // this has to be an empty string due to the internals of the AutoSuggest component
            });
            // Clear app state values if there are no options to select
            this.setValuesFromPayload();
        }
        this.setState({ options });
    };

    /**
     * Handler for the onChange Event for the Post Code field.
     * @param  {Object} event - the event triggered.
     */
    handlePostCodeChange = (event) => {
        if (event.type === 'react-blur') {
            // @todo figure out WTF this event is and why is results on onChange being called
            return;
        }

        const value = event.target.value;
        if (value.length === 4) {
            this.loadOptions(value);
        }
    };

    /**
     * Make an XHR call to the address service and update the options
     * @param  {String} value - The current input value
     */
    loadOptions = (value) => {
        const { dispatch, schema } = this.props;
        if (value.length === 4) {
            const pageState = pageContext.getProps().pageState;
            const apiSchema = schema.api.onPostcodeChange;
            const action = apiAction(apiSchema, pageState, [{ value }], apiSchema.baseUrlName);
            action.responseFunc = (response) => {
                dispatch(actions.setValue(`${this.id}.postcode`, value, schema.stateKey));
                this.setSuburbs(response);
                this.setStateOrTerritory(response);
            };
            dispatch(action);
        }
    };

    handleSuburbSelected = () => {
        const { stateValues } = this.props;
        this.performSuburbSelectedAction(stateValues.suburb, stateValues.postCode);
    };

    performSuburbSelectedAction = (suburb, postCode) => {
        const { schema, dispatch } = this.props;
        const suburbSelectedSchema = schema.api.onSuburbSelected;
        if (suburbSelectedSchema) {
            const pageState = pageContext.getProps().pageState;
            const action = apiAction(suburbSelectedSchema, pageState, [{ postCode, suburb }], suburbSelectedSchema.baseUrlName);
            dispatch(action);
        }
    };

    /**
     * Set the suburbs options for the suburb select from the response returned from the API call
     * @param {Object} response - The response from the address service
     */
    setSuburbs = (response) => {
        const parsedResponse = objValues(response) || [];
        const { touches } = this.state;
        const { stateValues, schema, dispatch } = this.props;
        const suburbs = parsedResponse.map((suburbPayload) => { return { label: suburbPayload.suburb, value: suburbPayload.suburb }; });
        const newState = { suburbs: [{ label: 'Suburb', value: '' }].concat(suburbs) };
        if (suburbs.length) {
            const suburb = stateValues.suburb ? stateValues.suburb : suburbs[0].value;
            dispatch(actions.setValue(`${this.id}.suburb`, suburb, schema.stateKey));
            newState.touches = Object.assign(touches, { suburb: true });
            this.performSuburbSelectedAction(suburb, response[0].postcode);
        }
        this.setState(newState);
    };

    /**
     * Set the state or territory to state from the response of the postcode lookup
     * @param {Object} response - The response from the address service
     */
    setStateOrTerritory = (response) => {
        const { dispatch, schema } = this.props;
        // added check to prevent expection being thrown when user inputs a postcode that isn't valid
        if (response[0] && response[0].state) {
            dispatch(
                actions.setValue(`${this.id}.state`, response[0].state, schema.stateKey)
            );
        }
    };

    /**
     * Create and return a FormElement (eg, input, select) for an MFA subcomponent
     * @param {Object} elementSchema - The schema for the MFA subcomponent element
     * @returns {Node} - The MFA subcomponent
     */
    getFormElement = (elementSchema) => {
        const { name } = elementSchema;
        const { touches } = this.state;
        const { elementRules, dispatch, stateValues } = this.props;
        const formElementFactory = pageContext.getProps().factories.formElement;
        const onBlur = (event) => { this.handleValueChangeFromEvent(event, name, event.target.value); };
        const onChange = this.onChangeFuncs[name] || null;

        if (this.updateSchemaFuncs[name]) {
            this.updateSchemaFuncs[name](elementSchema);
        }

        return formElementFactory.createElement(
            elementSchema,
            elementRules[name],
            touches[name],
            dispatch,
            stateValues[name] || '',
            {},
            null,
            { onBlur, onChange }
        );
    };

    /**
     * Build element field row label
     * @param {Object} element - The schema for the MFA subcomponent element
     * @returns {Node} - The element field row label
     */
    getFieldElementLabel = (element) => {
        const optional = element.field.optional ? (
            <span className={labelStyles.variant_optional}>Optional</span>
        ) : null;
        return (
            <SimpleFlexbox display="flex" contentBetween="xs">
                <label className={labelStyles.variant_default}>{element.label}</label>
                {optional}
            </SimpleFlexbox>
        );
    };

    /**
     * Iterate through the rows defined in props.elementSchema to build the structure.
     * @returns {Node} The Flexbox with each rendered row and components
     */
    getFieldGroupRows = () => {
        const { schema, elementSchema } = this.props;
        const rowStyle = schema.field.addonWidth === 'full_width' ? styles.row_full_width : styles.row;
        return elementSchema.rows.map((row) => {
            const elements = row.map((element) => {
                return (
                    <SimpleFlexbox key={`${this.id}_fieldGroup_${element.name}`} className={styles.field}>
                        {this.getFieldElementLabel(element)}
                        {this.getFormElement(element)}
                    </SimpleFlexbox>
                );
            });
            return (
                <SimpleFlexbox key={`${this.id}_multiFieldGroup_${row[0].name}`} className={rowStyle}>
                    {elements}
                </SimpleFlexbox>
            );
        });
    };

    checkRules = () => {
        const { touches } = this.state;
        const { invalidElements, rules } = this.props;
        const { elementRule, rulesResults } = rulesCache.getAllRuleData(rules.validation.id);
        const { errorMessages, ruleTouches } = elementRule.props;

        const failingAddressRules = Object.keys(ruleTouches).reduce((acc, elementName) => {
            const ruleNames = ruleTouches[elementName];
            for (const ruleName of ruleNames) {
                if  (!rulesResults[ruleName] && (invalidElements.size || touches[elementName])) {
                    acc.push(ruleName);
                }
            }
            return acc;
        }, []);

        // There could be more than one failing validation rule, we don't really care which one is shown as long as all
        // validation errors are fixed by the user
        return errorMessages[failingAddressRules[0]];
    };

    /**
     * Get a validationMessage component with an error message if there are errors
     * @returns {Node} - The validation message
     */
    getValidationMessage = () => {
        if (get(this.props, 'rules.validation.result', true)) {
            return null;
        }

        // There could be more than one failing validation rule, we don't really care which one is shown as long as all
        // validation errors are fixed by the user
        const errorMessage = this.checkRules();
        return errorMessage && (
            <span className={validationStyles.error}>
                {errorMessage}
            </span>
        );
    };

    render = () => {
        return (
            <>
                {this.getFieldGroupRows()}
                {this.getValidationMessage()}
            </>
        );
    };

}

_MultiFieldAddress.propTypes = {
    // The initial set of values, loaded from state for MFA subcomponents, eg: {postcode: '4000', suburb: 'Brisbane', streetName: '', streetNumber: ''}
    stateValues: PropTypes.object.isRequired,
    // The top level, quote_form_schema for this MFA component instance
    schema: PropTypes.object.isRequired,
    // The low level subComponent definition schema; getMFASchema(props.schema)
    elementSchema: PropTypes.object.isRequired,
    // Rules for the individual elements, eg { postcode: <rules>, suburb: <rules>} etc
    elementRules: PropTypes.object.isRequired,
    // Overall rules for the MFA component
    rules: PropTypes.object.isRequired,
    // The current set of invalid elements that result from validating a page, this lets us know if a validation message should be displayed
    invalidElements: PropTypes.object.isRequired,
    // redux dispatch function
    dispatch: PropTypes.func.isRequired
};

/**
 * Get the value for an MFA element from state. If the element should be preloaded from another part of state, return that value
 * @param {Object} state - The app state
 * @param {Object} element - The element schema for the MFA subcomponent
 * @returns {String} - The state value for the element
 */
const getStateValue = (state, element) => {
    const form = quoteFormSelector(state);
    if (element.preloadFrom) {
        return form.getIn(element.preloadFrom.split('.'));
    }
    return form.getIn(element.identifier.split('.'));
};

const mapStateToProps = (state, props) => {
    const elementSchema = getMFASchema(props.schema);
    const form = quoteFormSelector(state);
    const stateValues = {};
    stateValues.state = form.getIn(elementSchema.stateOrTerritoryStateKey.split('.'));

    const elementRules = {};
    for (const row of elementSchema.rows) {
        for (const element of row) {
            stateValues[element.name] = getStateValue(state, element);
            elementRules[element.name] = elementRulesStateToProps()(state, { schema: element }).rules;
        }
    }

    const invalidElements = state.getIn(['vertical', 'quote', 'validation', 'invalidElements']);
    return { elementSchema, elementRules, invalidElements, stateValues };
};

export const MultiFieldAddress = connect(mapStateToProps)(_MultiFieldAddress);

export { _MultiFieldAddress };

export const testExports = {
    getStateValue,
    mapStateToProps
};
